import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FolderOpen, ArrowRight, Calendar, Trash, MapPin, User } from "@phosphor-icons/react";

const STATUS_STYLES = {
  draft: "bg-ink-800 text-ink-400 border-ink-700",
  analyzing: "bg-blueprint/10 text-blueprint border-blueprint/40",
  ready: "bg-amber/10 text-amber border-amber/40",
  sent: "bg-emerald-500/10 text-emerald-500 border-emerald-500/40",
};

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", client_name: "", location: "", notes: "" });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/projects");
      setProjects(r.data);
    } catch {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) return toast.error("Project name is required");
    setCreating(true);
    try {
      const r = await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", client_name: "", location: "", notes: "" });
      navigate(`/projects/${r.data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.project_id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="data-label text-amber mb-1">Workspace</div>
          <h1 className="font-heading text-4xl tracking-tight font-medium">Projects</h1>
          <p className="text-ink-400 mt-1 text-sm">{projects.length} estimate{projects.length !== 1 ? "s" : ""}</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-project-button"
              className="bg-amber hover:bg-amber-hover text-ink-950 font-semibold h-10 rounded-md transition-colors duration-150"
            >
              <Plus size={16} weight="bold" className="mr-1.5" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-ink-900 border-ink-700 text-ink-50">
            <DialogHeader>
              <DialogTitle className="font-heading">New project</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="data-label">Project name</Label>
                <Input
                  data-testid="project-name-input"
                  autoFocus
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Smith Residence Kitchen"
                  className="bg-ink-950 border-ink-700 mt-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="data-label">Client</Label>
                  <Input
                    data-testid="project-client-input"
                    value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-ink-950 border-ink-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="data-label">Location</Label>
                  <Input
                    data-testid="project-location-input"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Brooklyn, NY"
                    className="bg-ink-950 border-ink-700 mt-2"
                  />
                </div>
              </div>
              <div>
                <Label className="data-label">Notes</Label>
                <Textarea
                  data-testid="project-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  placeholder="Scope notes, special instructions…"
                  className="bg-ink-950 border-ink-700 mt-2"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="cancel-create-project">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  data-testid="confirm-create-project"
                  className="bg-amber hover:bg-amber-hover text-ink-950 font-semibold"
                >
                  {creating ? "Creating…" : "Create project"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Body */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-44 rounded-md bg-ink-900 border border-ink-700 animate-pulse" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="border border-ink-700 rounded-md bg-ink-900 overflow-hidden">
          <div className="relative p-12 text-center">
            <div
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage: "url(https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div className="relative z-10">
              <div className="mx-auto h-14 w-14 rounded-md border border-ink-700 bg-ink-800 flex items-center justify-center text-amber mb-4">
                <FolderOpen size={28} weight="duotone" />
              </div>
              <h3 className="font-heading text-2xl tracking-tight font-medium mb-2">No projects yet</h3>
              <p className="text-ink-400 mb-6 max-w-md mx-auto">
                Create your first project, drop in a set of shop drawings, and let the AI build a takeoff for you.
              </p>
              <Button
                data-testid="empty-create-button"
                onClick={() => setOpen(true)}
                className="bg-amber hover:bg-amber-hover text-ink-950 font-semibold"
              >
                <Plus size={16} weight="bold" className="mr-1.5" />
                Create your first project
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-grid">
          {projects.map((p) => {
            const created = new Date(p.created_at);
            const items = (p.line_items || []).length;
            const drawings = (p.drawings || []).length;
            const statusCls = STATUS_STYLES[p.status] || STATUS_STYLES.draft;
            return (
              <div
                key={p.project_id}
                className="group border border-ink-700 bg-ink-900 rounded-md p-5 hover:border-amber/60 transition-colors duration-150 flex flex-col"
                data-testid={`project-card-${p.project_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className={`text-[10px] tracking-[0.18em] uppercase font-bold border rounded-sm px-2 py-0.5 ${statusCls}`}>
                    {p.status}
                  </span>
                  <button
                    onClick={() => remove(p.project_id)}
                    className="text-ink-500 hover:text-red-400 transition-colors"
                    title="Delete"
                    data-testid={`delete-project-${p.project_id}`}
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
                <Link to={`/projects/${p.project_id}`} className="flex-1 flex flex-col">
                  <h3 className="font-heading text-lg tracking-tight font-medium mb-2 line-clamp-2">{p.name}</h3>
                  <div className="space-y-1 mb-4 text-sm">
                    {p.client_name && (
                      <div className="flex items-center gap-1.5 text-ink-400">
                        <User size={13} /> <span className="truncate">{p.client_name}</span>
                      </div>
                    )}
                    {p.location && (
                      <div className="flex items-center gap-1.5 text-ink-400">
                        <MapPin size={13} /> <span className="truncate">{p.location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-ink-500">
                      <Calendar size={13} /> {created.toLocaleDateString()}
                    </div>
                  </div>
                  <div className="mt-auto flex items-center justify-between pt-3 border-t border-ink-700">
                    <div className="flex gap-4 text-xs num">
                      <span className="text-ink-400">{drawings} <span className="data-label inline">files</span></span>
                      <span className="text-ink-400">{items} <span className="data-label inline">items</span></span>
                    </div>
                    <span className="text-amber group-hover:translate-x-0.5 transition-transform">
                      <ArrowRight size={16} weight="bold" />
                    </span>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
